import { LegacyRef } from 'react';
import classnames from 'classnames';

import Color from 'src/constants/color';

import styles from 'src/components/Box/box.less';

type BoxStyleProps = {
    flexDirection: 'row' | 'row-reverse' | 'column' | 'column-reverse';
    justifyContent: 'flex-start' | 'flex-end' | 'center' | 'space-between' | 'space-around' | 'space-evenly';
    alignItems: 'baseline' | 'center' | 'flex-start' | 'flex-end' | 'stretch';
    flexGrow: number;
    spacing: number;
    padding: number | string;
    margin: number;
    background: Color;
    borderRadius: number;
    /**
     * Referencing to root div-element
     */
    boxRef: LegacyRef<HTMLDivElement>;
};

export type BoxProps = React.HTMLAttributes<HTMLElement> & Partial<BoxStyleProps>;

const normalizeCssVariables = (variables: Record<string, string | number | undefined>): React.CSSProperties => {
    return Object.entries(variables).reduce((acc, [key, value]) => {
        if (value) {
            acc[key] = value;
        }
        return acc;
    }, {} as Record<string, string | number>);
};

export const Box = ({
    spacing = 0,
    flexDirection,
    alignItems,
    padding = 0,
    margin = 0,
    justifyContent,
    background,
    borderRadius = 0,
    boxRef,
    flexGrow,
    ...props
}: BoxProps): JSX.Element => {
    const boxStyleVariables = {
        '--box-flex-direction': flexDirection,
        '--box-justify-content': justifyContent,
        '--box-align-items': alignItems,
        '--box-spacing': spacing,
        '--box-padding': padding,
        '--box-margin': margin,
        '--box-background': background,
        '--box-border-radius': borderRadius,
        '--box-flex-grow': flexGrow,
    };

    const cssVariables = normalizeCssVariables(boxStyleVariables);

    const boxCssClasses = classnames(
        styles.box,
        {
            [styles.boxAlignItems]: Boolean(alignItems),
            [styles.boxGap]: Boolean(spacing),
            [styles.boxJustifyContent]: Boolean(justifyContent),
            [styles.boxFlexDirection]: Boolean(flexDirection),
            [styles.boxPadding]: Boolean(padding),
            [styles.boxMargin]: Boolean(margin),
            [styles.boxBorderRadius]: Boolean(borderRadius),
            [styles.boxBackgroundColor]: Boolean(background),
            [styles.boxFlexGrow]: Boolean(flexGrow),
        },
        props.className
    );

    return <div {...props} className={boxCssClasses} style={{ ...cssVariables, ...props.style }} ref={boxRef} />;
};

export default Box;
